import React from 'react';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';

export interface RowSelectionInstance<T = any> {
    selectedIds: Set<T>;
    selectionMode: boolean;
    toggleSelect: (id: T) => void;
    toggleSelectAll: () => void;
    selectAll: () => void;
    clearSelection: () => void;
    handleRowSelect: (id: T, event?: React.MouseEvent) => void;
    handleRowMouseDown: (id: T, event: React.MouseEvent) => void;
    handleRowMouseUp: () => void;
    handleRowMouseLeave: () => void;
    wasLongPress: () => boolean;
    selectedCount: number;
    allSelected: boolean;
}

interface DataTableRowProps {
    /** The ID of the row item */
    id: string | number;
    /** The rowSelection instance returned from useRowSelection */
    rowSelection?: RowSelectionInstance<any>;
    /** Click handler for navigating to details (ignored if in selection mode or was long press) */
    onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
    className?: string;
    children: React.ReactNode;
}

export function DataTableRow({
    id,
    rowSelection,
    onClick,
    className,
    children
}: DataTableRowProps) {
    const isSelected = rowSelection?.selectedIds?.has(id);
    const isClickable = Boolean(onClick || rowSelection?.selectionMode);

    return (
        <tr
            tabIndex={isClickable ? 0 : undefined}
            className={cn(
                'transition-colors border-b border-slate-200',
                isClickable && 'cursor-pointer select-text',
                isSelected ? 'bg-indigo-50/60' : 'hover:bg-slate-50',
                className
            )}
            onClick={(e) => {
                if (rowSelection?.wasLongPress?.()) return;
                // Guard: if user is highlighting/selecting text to copy, do not navigate
                const selection = typeof window !== 'undefined' ? window.getSelection()?.toString() : '';
                if (selection && selection.trim().length > 0) return;

                if (rowSelection?.selectionMode) {
                    rowSelection.handleRowSelect(id, e);
                    return;
                }
                onClick?.(e);
            }}
            onKeyDown={(e) => {
                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                    if (e.target === e.currentTarget) {
                        e.preventDefault();
                        onClick?.(e as any);
                    }
                }
            }}
            onMouseDown={(e) => rowSelection?.handleRowMouseDown?.(id, e)}
            onMouseUp={rowSelection?.handleRowMouseUp}
            onMouseLeave={rowSelection?.handleRowMouseLeave}
        >
            {children}
        </tr>
    );
}

/**
 * Convenience component for rendering the checkbox cell in a DataTableRow
 */
export function DataTableRowCheckbox({
    id,
    rowSelection,
    className,
    asChild
}: {
    id: string | number;
    rowSelection?: RowSelectionInstance<any>;
    className?: string;
    asChild?: boolean;
}) {
    if (!rowSelection) return asChild ? null : <td className={cn("apv-col-checkbox px-2 py-2", className)} />;
    
    const checkbox = (
        <Checkbox 
            checked={rowSelection.selectedIds.has(id)} 
            onCheckedChange={() => rowSelection.toggleSelect(id)} 
        />
    );

    if (asChild) {
        return checkbox;
    }

    return (
        <td className={cn("apv-col-checkbox", className)} onClick={e => e.stopPropagation()}>
            {checkbox}
        </td>
    );
}

/**
 * Convenience component for rendering the select-all checkbox in the table header
 */
export function DataTableSelectAllCheckbox({
    rowSelection,
    className,
    asChild
}: {
    rowSelection?: RowSelectionInstance<any>;
    className?: string;
    asChild?: boolean;
}) {
    if (!rowSelection) return asChild ? null : <th className={cn("apv-col-checkbox w-[40px] px-2 py-2", className)} />;
    
    // Luôn hiện ô chọn tất cả (trước đây chỉ hiện sau khi đã chọn 1 dòng → người dùng không biết có chọn nhanh)
    const checkbox = (
        <Checkbox
            aria-label="Chọn tất cả dòng trên trang"
            checked={rowSelection.allSelected}
            onCheckedChange={rowSelection.toggleSelectAll}
        />
    );

    if (asChild) {
        return checkbox;
    }

    return (
        <th className={cn("apv-col-checkbox w-[40px] text-center", className)} onClick={e => e.stopPropagation()}>
            {checkbox}
        </th>
    );
}
